import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";

interface ImageDropzoneSvgProps {
  onImageSelected: (selected: File | null) => void;
  base64: string | File;
  width?: number;
  height?: number;
  uploadMessage?: string;
  mimeType?: string;
}

const DropContainer = styled.div<{ isDragActive: boolean; isDragReject: boolean }>`
  border: 2px dashed #cccccc;
  border-radius: 4px;
  width: 100%;
  height: 150px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: ${(props) =>
    props.isDragActive ? "#e0f7fd" : props.isDragReject ? "#ffd7d7" : "white"};
  &:hover {
    border-color: #3884cc;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UploadedImage = styled.img<{ width?: number; height?: number }>`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const ChangeImageButton = styled.button`
  position: absolute;
  bottom: 5px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 255, 255, 0.8);
  border: none;
  padding: 5px;
  cursor: pointer;
  font-size: 12px;
  border-radius: 4px;
`;

const UploadMessage = styled.p`
  text-align: center;
  font-size: 16px;
  color: #999999;
  &:hover {
    color: #3884cc;
  }
`;

const ErrorMessage = styled.p`
  color: red;
  text-align: center;
`;

const ImageDropzoneSvg: React.FC<ImageDropzoneSvgProps> = ({
  onImageSelected,
  base64,
  width,
  height,
  uploadMessage,
  mimeType,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!base64) {
      setSelectedImage(null);
      return;
    }
  
    if (typeof base64 === "string") {
      let formatted = base64;
      if (!formatted.startsWith("data:") && !formatted.startsWith("http")) {
        const mime = mimeType ? mimeType : "image/png";
        formatted = `data:${mime};base64,${formatted}`;
      }
      setSelectedImage(formatted);
    } 
    else if (base64 instanceof File) {
      convertBase64(base64)
        .then((result) => {
          let formatted = result;
          if (!result.startsWith("data:") && !result.startsWith("http")) {
            const mime = mimeType ? mimeType : "image/png";
            formatted = `data:${mime};base64,${result}`;
          }
          setSelectedImage(formatted);
        })
        .catch((error) => {
          console.error("Erro convertendo o arquivo em Base64:", error);
          setSelectedImage(null);
        });
    }
  }, [base64, mimeType]);  

  const convertBase64 = async (file: File): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result as string);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 1) {
        setError("Você só pode enviar uma imagem.");
        setSelectedImage(null);
        onImageSelected(null);
      } else if (acceptedFiles.length === 1) {
        setError(null);
        const file = acceptedFiles[0];
        const base64Result = await convertBase64(file);
        setSelectedImage(base64Result);
        onImageSelected(file);
      }
    },
    [onImageSelected]
  );

  const handleChangeImage = () => {
    setSelectedImage(null);
    setError(null);
    onImageSelected(null);
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: {
      "image/png": [".png"],
      "image/svg+xml": [".svg"],
      "image/jpeg": [".jpeg", ".jpg"],
    },
    onDrop: handleDrop,
  });

  return (
    <DropContainer {...getRootProps({ isDragActive, isDragReject })}>
      <input {...getInputProps()} />
      {selectedImage ? (
        <ImageContainer>
          <UploadedImage src={selectedImage} width={width} height={height} />
          <ChangeImageButton onClick={handleChangeImage}>Alterar imagem</ChangeImageButton>
        </ImageContainer>
      ) : isDragActive ? (
        <UploadMessage>{uploadMessage || "Arraste o arquivo aqui..."}</UploadMessage>
      ) : (
        <UploadMessage
          dangerouslySetInnerHTML={{
            __html: uploadMessage || "Arraste a imagem aqui ou clique para selecionar.",
          }}
        />
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </DropContainer>
  );
};

export { ImageDropzoneSvg };
